import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { LinkPrimitive } from '#pplx/frontend/aether/primitives/LinkPrimitive/index.js';
import { ComputerCommandMenuSkillDetailsAction } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/ComputerCommandMenuSkillDetailsAction.js';
import { PluginCommandMenuItems } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/plugins/PluginCommandMenuItems.js';
import { useComputerCommandMenuData } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/useComputerCommandMenuData.js';
import { useComputerCommandMenuSelection } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/useComputerCommandMenuSelection.js';
import { useLegacyComputerCommandMenuEditor } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/useLegacyComputerCommandMenuEditor.js';
import { useAskInputSkills } from '#pplx/frontend/apps/ask/ask-input/ask-input-plus-menu/hooks/useAskInputSkills.js';
import { usePluginsNavigationHrefs } from '#pplx/frontend/apps/ask/connectors/hooks/usePluginsNavigationHrefs.js';
import { useIsIncognito } from '#pplx/frontend/apps/ask/incognito/useIsIncognito.js';
import { useSkillModal } from '#pplx/frontend/apps/ask/skills/hooks/useSkillModal.js';
import { toPublicWebUrl } from '#pplx/frontend/common/location.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { memo, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
/** Web controller for the Computer command menu: cloud catalog with
 * server-side search and pagination, cloud skill insertion, and skill details. */
export const WebCommandMenuItems = memo(function WebCommandMenuItems({ collectionUuid, skillMentionEditorRef, isFollowUp = false, searchQuery, sideChatAvailable, registerInputKeyDownHandler, appOpenModal, openConnectorModal, onUiActionSelect, onMenuClose, hideCommandItems = false, hideSettingsAction = false, onOpenSettings, }) {
    const intl = useIntl();
    const { skills: skillsHref } = usePluginsNavigationHrefs();
    const { track } = useInstrumentation();
    const { isIncognito, isAlwaysIncognitoRequired } = useIsIncognito({
        reason: 'computer-command-menu',
    });
    const { openModal: localOpenModal } = useModalContext();
    const openModal = appOpenModal ?? localOpenModal;
    const openSkillModal = useSkillModal({ openModal, openConnectorModal });
    const skillsProps = useAskInputSkills({
        showSkills: true,
        lexicalEditorRef: skillMentionEditorRef,
        collectionUuid,
        prefixWithSlash: false,
        replaceActiveCommandQuery: true,
    });
    const { resolvedSearchQuery, presentation, showNoCustomSkillsPlaceholder, isLoading, pagination, } = useComputerCommandMenuData({
        searchQuery,
        collectionUuid,
        sideChatAvailable,
        intl,
        track,
        isIncognito,
        isFollowUp,
        isAlwaysIncognitoRequired,
        hideCommandItems,
        hideSettingsAction,
    });
    const getEditor = useLegacyComputerCommandMenuEditor({ skillMentionEditorRef });
    const { handleItemSelect, getUseButtonTrack } = useComputerCommandMenuSelection({
        getEditor,
        skillsProps,
        onMenuClose,
        openModal,
        isIncognito,
        onUiActionSelect,
        onOpenSettings,
        isFollowUp,
        isAlwaysIncognitoRequired,
        resolvedSearchQuery,
        track,
    });
    const handleSkillDetailsOpen = useCallback((skill) => {
        openSkillModal({
            skillId: skill.id,
            context: 'chat-thread',
        }, { replace: false });
    }, [openSkillModal]);
    const renderDetailActions = useCallback((item) => (_jsx(ComputerCommandMenuSkillDetailsAction, { item: item, onSkillDetailsOpen: handleSkillDetailsOpen })), [handleSkillDetailsOpen]);
    return (_jsx(PluginCommandMenuItems, { presentation: presentation, isLoading: isLoading, onItemSelect: handleItemSelect, registerInputKeyDownHandler: registerInputKeyDownHandler, emptySkillsPlaceholder: showNoCustomSkillsPlaceholder ? (_jsxs(_Fragment, { children: [_jsx(FormattedMessage, { defaultMessage: "No skills yet", description: "Placeholder shown when the user has no custom skills in the Computer command menu", id: "28f98ea7d9" }), _jsx("span", { "aria-hidden": "true", className: "px-1", children: _jsx(FormattedMessage, { defaultMessage: "\u2013", description: "Separator between empty skills placeholder text and learn more link", id: "c52ad45645" }) }), _jsx(LinkPrimitive, { href: toPublicWebUrl(skillsHref), className: "text-secondary cursor-pointer no-underline hover:underline", children: _jsx(FormattedMessage, { defaultMessage: "Learn more", description: "Link label for opening the Skills page from the Computer command menu empty state", id: "e574bc1a55" }) })] })) : undefined, pagination: pagination, renderDetailActions: renderDetailActions, getUseButtonTrack: getUseButtonTrack }));
});
//# sourceMappingURL=WebCommandMenuItems.js.map